import { Boxes, ChevronDown } from "lucide-react";
import type { ModelInfo } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Props {
  models: ModelInfo[];
  selected: string | null;
  onSelect: (providerKey: string) => void;
  compact?: boolean;
}

export function ProviderPicker({ models, selected, onSelect, compact = false }: Props) {
  const providers = Array.from(
    models.reduce<Map<string, string>>((items, model) => {
      items.set(model.provider, model.provider_name || model.provider);
      return items;
    }, new Map())
  ).sort(([, a], [, b]) => a.localeCompare(b));

  return (
    <label
      className={cn(
        "relative flex min-w-0 items-center rounded-lg border border-studio-border bg-studio-card transition-colors focus-within:border-studio-accent/55",
        compact ? "min-h-9 px-2.5" : "min-h-11 px-3.5"
      )}
    >
      <Boxes className={cn("shrink-0 text-studio-muted", compact ? "h-3.5 w-3.5" : "h-4 w-4")} />
      <span className="sr-only">Provider</span>
      <select
        aria-label="Provider"
        value={selected ?? ""}
        onChange={(event) => event.target.value && onSelect(event.target.value)}
        className={cn(
          "min-w-0 flex-1 appearance-none bg-transparent px-2 font-semibold text-studio-ink outline-none",
          compact ? "text-[11px]" : "text-sm"
        )}
      >
        <option value="">Select provider</option>
        {providers.map(([key, name]) => <option key={key} value={key}>{name}</option>)}
      </select>
      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-studio-muted" />
    </label>
  );
}
